<script setup lang="ts">
	import {
		BarElement,
		BarOptions,
		CategoryScale,
		Chart as ChartJS,
		Legend,
		LinearScale,
		Title,
		Tooltip,
	} from 'chart.js'
	import ChartDataLabels from 'chartjs-plugin-datalabels'
	import { computed } from 'vue'
	import { Bar } from 'vue-chartjs'

	import useCompanyReportHotelAnalytics from '@/modules/companies/composables/useCompanyReportHotelAnalytics'

	interface Props {
		companyId?: number
		dateRange?: string[] | null
		creationDateRange?: string[] | null
		statusFilter?: string
		groupByFilter?: string
		searchTravelerId?: number | null
		searchHotelId?: number | null
		createdAt?: string[] | null
		orderBy?: string
		orderDirection?: 'asc' | 'desc'
	}

	const props = defineProps<Props>()

	ChartJS.register(
		Title,
		Tooltip,
		Legend,
		BarElement,
		CategoryScale,
		LinearScale,
		ChartDataLabels
	)

	const { analytics, isFetching, isLoading } = useCompanyReportHotelAnalytics(
		computed(() => ({
			companyId: props.companyId,
			filters: {
				dates: props.dateRange,
				creationDates: props.creationDateRange,
				status: props.statusFilter !== 'all' ? props.statusFilter : undefined,
				groupBy: props.groupByFilter,
				travelerId: props.searchTravelerId || undefined,
				hotelId: props.searchHotelId || undefined,
				createdAt: props.createdAt || undefined,
				orderBy: props.orderBy || undefined,
				orderDirection: props.orderDirection || undefined,
			},
		}))
	)

	const topDestinationsBySpend = computed(() => {
		if (!analytics.value?.topDestinationsBySpend) return undefined
		return analytics.value?.topDestinationsBySpend
	})

	const hasValidTopDestinationsBySpend = computed(() => {
		if (!topDestinationsBySpend.value) return false
		return topDestinationsBySpend.value.length > 0
	})

	const chartData = computed(() => {
		if (
			!topDestinationsBySpend.value ||
			topDestinationsBySpend.value.length === 0
		) {
			return null
		}

		const sorted = [...topDestinationsBySpend.value].sort(
			(a, b) => b.totalSpend - a.totalSpend
		)

		return {
			labels: sorted.map((d) => `${d.city} (${d.cityCode})`),
			datasets: [
				{
					label: 'Gasto Total',
					data: sorted.map((d) => d.totalSpend),
					backgroundColor: [
						'rgba(59, 130, 246, 0.8)',
						'rgba(16, 185, 129, 0.8)',
						'rgba(245, 158, 11, 0.8)',
						'rgba(239, 68, 68, 0.8)',
						'rgba(139, 92, 246, 0.8)',
						'rgba(236, 72, 153, 0.8)',
					],
					borderColor: [
						'rgba(59, 130, 246, 1)',
						'rgba(16, 185, 129, 1)',
						'rgba(245, 158, 11, 1)',
						'rgba(239, 68, 68, 1)',
						'rgba(139, 92, 246, 1)',
						'rgba(236, 72, 153, 1)',
					],
					borderWidth: 1,
					borderRadius: 4,
				},
			],
		}
	})

	const chartOptions = {
		indexAxis: 'y' as const,
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: {
				display: false,
			},
			title: {
				display: true,
				text: 'Destinos con Mayor Gasto',
				font: {
					size: 14,
					weight: 'bold' as const,
				},
			},
			tooltip: {
				callbacks: {
					label: (context: any) => {
						const value = context.raw as number
						const destination =
							topDestinationsBySpend.value?.[context.dataIndex]
						if (destination) {
							return [
								`Gasto: $${value.toLocaleString('es-MX')} MXN`,
								`Reservaciones: ${destination.reservations}`,
								`Promedio por reserva: $${destination.averagePerReservation?.toLocaleString('es-MX')} MXN`,
							]
						}
						return `$${value.toLocaleString('es-MX')} MXN`
					},
				},
			},
			datalabels: {
				display: true,
				anchor: 'center' as const,
				color: '#FFFFFF',
				font: {
					weight: 'bold' as const,
					size: 11,
				},
				formatter: (value: number) => {
					return `$${(value / 1000).toFixed(1)}k`
				},
			},
		},
		scales: {
			x: {
				beginAtZero: true,
				grid: {
					display: true,
					color: 'rgba(0, 0, 0, 0.05)',
				},
				ticks: {
					callback: (value: number | string) => {
						const numValue =
							typeof value === 'string' ? parseFloat(value) : value
						return `$${(numValue / 1000).toFixed(0)}k`
					},
				},
			},
			y: {
				grid: {
					display: false,
				},
			},
		},
	}

	defineExpose({
		isLoading,
	})
</script>

<template>
	<div class="overflow-hidden rounded-lg bg-white shadow">
		<div class="px-4 py-5 sm:p-6 h-full">
			<div
				v-if="isLoading && !topDestinationsBySpend"
				class="flex items-center justify-center h-[300px]"
			>
				<div class="text-center">
					<span
						class="material-symbols-outlined mb-2 animate-spin text-4xl text-blue-ef-500"
					>
						progress_activity
					</span>
					<p class="text-sm text-gray-600">
						Cargando los destinos con mayor gasto...
					</p>
				</div>
			</div>

			<div
				v-else-if="chartData && hasValidTopDestinationsBySpend"
				class="h-[300px]"
			>
				<Bar
					:data="chartData"
					:options="chartOptions"
				/>
			</div>

			<div
				v-else
				class="flex items-center justify-center h-[300px]"
			>
				<div class="text-center">
					<span class="material-symbols-outlined mb-3 text-5xl text-gray-300">
						analytics
					</span>
					<p class="text-base font-medium text-gray-600">
						No hay datos disponibles para esta sección
					</p>
					<p class="mt-2 text-sm text-gray-500">
						No se encontraron destinos con mayor gasto
					</p>
				</div>
			</div>
		</div>
	</div>
</template>
